// Pro list mixin — behaviour shared by desktop lists built on the
// `pl-*` kit (src/sass/design-system/list/_pro-list.scss).
//
// Host component must provide:
//   - `headers`            v-data-table headers
//   - `items`              current page rows
//   - `proListKey`         computed string, unique per list (persistence)
//   - `proListLocked`      computed array of header values that can't be hidden
//   - ref `plTable`        on the `.pl-table` wrapper, with
//                          @mousedown="onCellDown" @mouseover="onCellOver"
// It gives you:
//   proListRootStyle, shownHeaders, toggleableHeaders, hiddenColumns,
//   toggleColumn(), plTableH (fit table height), cellSel {count,numCount,sum,avg},
//   clearCellSel(), faNum().
// UI-only state: do not use on pages that post `this.$data` to the API.

import PlTags from '@/components/proList/PlTags.vue'

export default {
  components: { PlTags },
  // Remember the list request getItems sends (url + params) so Print Studio can
  // repeat it page by page without each page extracting its params. Shadows
  // the global $axios on this instance only.
  // Embedded usage flag; most list pages already declare it (merged safely).
  props: ['mobileShow'],
  created() {
    var orig = this.$axios;
    if (!orig || orig.__plWrapped) return;
    var app = this;
    var origGet = orig.get;
    var wrapped = Object.create(orig);
    // defineProperty (not assignment): never go through an inherited setter.
    Object.defineProperty(wrapped, '__plWrapped', { value: true });
    Object.defineProperty(wrapped, 'get', {
      value: function (url, cfg) {
        var p = cfg && cfg.params;
        if (p && Object.prototype.hasOwnProperty.call(p, 'page') && !p.print && !p.lock && !p.deleteItems && !p.groupPrint) {
          // Kept out of reactive data: request params may hold large/circular objects.
          app._plLastList = Object.freeze({ url: url, params: p });
        }
        return origGet.apply(orig, arguments);
      },
    });
    this.$axios = wrapped;
  },
  // The General mixin's beforeMount replaces `options` (money settings) without
  // sortBy/page; many pages' options watchers map options.sortBy, and on mobile
  // (or before the table mounts) nothing fills it. Seed it in the same tick so
  // the watcher still runs only once.
  beforeMount() {
    var key = this.tableOptions ? 'tableOptions' : 'options';
    var cur = this[key];
    if (cur && typeof cur === 'object' && !Array.isArray(cur.sortBy)) {
      this[key] = Object.assign({}, cur, { sortBy: [], sortDesc: [], page: cur.page || 1, itemsPerPage: cur.itemsPerPage > 5 ? cur.itemsPerPage : 10 });
    }
  },
  data: () => ({
    hiddenColumns: [],
    printStudio: false,
    plTableH: 380,
    cellSel: { count: 0, numCount: 0, sum: 0, avg: 0 },
  }),
  computed: {
    // Desktop chrome from 800px (same breakpoint as the global isMobile);
    // embedded lists (mobileShow) always use it. Pages may override.
    isDesktopListLayout() {
      if (this.mobileShow) return true;
      return this.$vuetify.breakpoint.width >= 800;
    },
    // Defaults; pages with their own column-settings key / locked set override these.
    proListKey() {
      return 'pl-cols-' + this.plReportKey;
    },
    proListLocked() {
      // Combined #/⋮ column uses value `actions` or `action` (no separate `row`).
      return ['actions', 'action'];
    },
    // Current page for the mobile list (tableOptions or options, whichever the page uses).
    plPage() {
      var o = this.tableOptions || this.options || {};
      return o.page || 1;
    },
    plPrintCols() {
      return this.plPrintColumns(this.plPrintOverrides || {});
    },
    // Print Studio report key (templates are stored per key on the server).
    plReportKey() {
      return 'list-' + ((this.$route && this.$route.name) || this.$options.name || 'list');
    },
    // Print Studio data source: current rows, or the same list request repeated.
    plPrintSource() {
      var app = this;
      return {
        currentRows: function () { return app.items || []; },
        total: function () { return app.totalItems || (app.items || []).length; },
        fetchPage: function (page, perPage) {
          var last = app._plLastList;
          if (!last) return Promise.resolve({ rows: app.items || [], lastPage: 1, total: (app.items || []).length });
          var params = Object.assign({}, last.params, { page: page });
          params.options = Object.assign({}, last.params.options || {}, { page: page, itemsPerPage: perPage });
          return app.$axios.get(last.url, { params: params }).then(function (res) {
            var d = res.data || {};
            var rows = Array.isArray(d.data) ? d.data : Array.isArray(d) ? d : [];
            return { rows: rows, lastPage: d.last_page || 1, total: d.total || rows.length };
          });
        },
      };
    },
    // Vuetify pager icons: LTR meaning; Vuetify mirrors them in RTL.
    plFooterProps() {
      return {
        showFirstLastPage: true,
        itemsPerPageOptions: [10, 25, 50, 100, 500],
        firstIcon: 'mdi-chevron-double-left',
        lastIcon: 'mdi-chevron-double-right',
        prevIcon: 'mdi-chevron-left',
        nextIcon: 'mdi-chevron-right',
      };
    },
    proListRootStyle() {
      var theme = this.$vuetify.theme;
      var t = theme.themes[theme.isDark ? 'dark' : 'light'] || {};
      var primary = typeof t.primary === 'string' ? t.primary : (t.primary && t.primary.base) || '#1976d2';
      return { '--pl-primary': primary, '--pl-primary-soft': primary + '1a' };
    },
    shownHeaders() {
      var hidden = this.hiddenColumns;
      return (this.headers || []).filter(function (h) {
        return hidden.indexOf(h.value) === -1;
      });
    },
    // Headers for the table: the page's own visible set (showHeaders, if it
    // has one) minus columns hidden from the column menu. Pages may override.
    tableHeaders() {
      var hidden = this.hiddenColumns;
      var base = this.showHeaders || this.headers || [];
      return base.filter(function (h) {
        return hidden.indexOf(h.value) === -1;
      });
    },
    toggleableHeaders() {
      var locked = this.proListLocked || [];
      return (this.headers || []).filter(function (h) {
        return locked.indexOf(h.value) === -1;
      });
    },
  },
  watch: {
    hiddenColumns(val) {
      try {
        localStorage.setItem(this.proListKey + '-hidden', JSON.stringify(val));
      } catch (e) {}
      this.$nextTick(this.plMeasure);
    },
    items() {
      this.clearCellSel();
      this.$nextTick(this.plMeasure);
    },
  },
  mounted() {
    try {
      var saved = JSON.parse(localStorage.getItem(this.proListKey + '-hidden'));
      if (Array.isArray(saved)) this.hiddenColumns = saved;
    } catch (e) {}
    window.addEventListener('resize', this.plMeasure);
    window.addEventListener('mouseup', this.onCellUp);
    window.addEventListener('keydown', this.onCellKey);
    window.addEventListener('mousedown', this.onDocDown, true);
    this.$nextTick(this.plMeasure);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.plMeasure);
    window.removeEventListener('mouseup', this.onCellUp);
    window.removeEventListener('keydown', this.onCellKey);
    window.removeEventListener('mousedown', this.onDocDown, true);
  },
  methods: {
    // The table wrapper: a plain ref="plTable" div, or <pro-list-frame ref="plTable">.
    plTableEl() {
      var r = this.$refs.plTable;
      var el = r && r.$refs && r.$refs.table ? r.$refs.table : r && r.$el ? r.$el : r;
      // Only real elements (a frame that failed to render leaves a comment node).
      return el && el.nodeType === 1 ? el : null;
    },
    faNum(v) {
      if (v === null || v === undefined) return v;
      var str = String(v);
      if (!(this.$i18n && String(this.$i18n.locale).indexOf('fa') === 0)) return str;
      return str.replace(/[0-9]/g, function (d) {
        return '۰۱۲۳۴۵۶۷۸۹'[d];
      });
    },
    // Value at a dotted path ("user.name"); objects collapse to a readable name.
    plValue(row, path) {
      var v = String(path).split('.').reduce(function (o, k) { return o == null ? o : o[k]; }, row);
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        v = v.name || v.title || [v.firstname, v.lastname].filter(Boolean).join(' ') || '';
      }
      if (Array.isArray(v)) v = v.map(function (x) { return x && (x.text || x.name) ? x.text || x.name : x; }).join('، ');
      return v;
    },
    // Print Studio columns built from the table headers.
    // overrides: { [headerValue]: { format, sum, value(row), label, width, defaultVisible } | false }
    plPrintColumns(overrides) {
      var app = this;
      var skip = ['actions', 'row', '__select', 'data-table-select'];
      overrides = overrides || {};
      return (this.headers || [])
        .filter(function (h) { return skip.indexOf(h.value) === -1 && overrides[h.value] !== false; })
        .map(function (h) {
          var o = overrides[h.value] || {};
          return Object.assign(
            { key: h.value, label: h.text, width: 1, value: function (r) { return app.plValue(r, h.value); } },
            o
          );
        });
    },
    // Mobile paging: drive whichever options object the page's watcher uses.
    plLoadPage(page) {
      var key = this.tableOptions ? 'tableOptions' : 'options';
      var cur = this[key] || {};
      this[key] = Object.assign({}, cur, {
        page: page,
        itemsPerPage: cur.itemsPerPage > 5 ? cur.itemsPerPage : 10,
        sortBy: cur.sortBy || [],
        sortDesc: cur.sortDesc || [],
      });
    },
    // Generic mobile card built from the table headers (pages may override).
    plAutoCard(item) {
      var app = this;
      var skip = ['actions', 'action', 'row', '__select', 'id', 'data-table-select', 'tags', 'head.tags'];
      var hs = (this.tableHeaders || this.headers || []).filter(function (h) { return skip.indexOf(h.value) === -1; });
      var text = function (h) {
        var v = app.plValue(item, h.value);
        if (v === null || v === undefined || v === '') return '';
        if (typeof v === 'number' || /^-?\d+(\.\d+)?$/.test(String(v))) {
          return app.faNum(Math.abs(Number(v)) >= 1000 ? app.$numberFormat(v) : v);
        }
        return app.faNum(v);
      };
      var titleH = hs.find(function (h) { return /name|title|number|content/i.test(h.value); }) || hs[0];
      var descH = hs.find(function (h) { return h !== titleH && /content|description|desc|comment/i.test(h.value); });
      var rest = hs.filter(function (h) { return h !== titleH && h !== descH; });
      var rows = rest.map(function (h) { return { label: h.text, value: text(h), num: /\d/.test(text(h)) }; });
      var tags = this.plValue(item, 'tags') || [];
      return {
        title: titleH ? text(titleH) : '',
        subtitle: descH ? text(descH) : '',
        badge: item && item.id != null ? '#' + this.faNum(item.id) : '',
        chips: Array.isArray(item && item.tags) ? item.tags.map(function (t) { return { text: t.text || t.name, color: t.color }; }) : [],
        rows: rows.slice(0, 5),
        details: rows,
      };
    },
    // Generic mobile actions: whatever the page already offers.
    plAutoActions(item) {
      var a = [];
      if (typeof this.edit === 'function') a.push({ icon: 'mdi-pencil-outline', label: this.$t('edit'), handler: this.edit });
      if (typeof this.openDeleteDialog === 'function') {
        a.push({ icon: 'mdi-trash-can-outline', label: this.$t('remove'), danger: true, handler: this.openDeleteDialog });
      }
      return a;
    },
    toggleColumn(value) {
      var i = this.hiddenColumns.indexOf(value);
      this.hiddenColumns = i === -1
        ? this.hiddenColumns.concat([value])
        : this.hiddenColumns.filter(function (v) { return v !== value; });
    },
    // Fit the table so its footer (per page / pager / go-to) stays on screen.
    plMeasure() {
      var wrap = this.plTableEl();
      if (!wrap || this.mobileShow || typeof wrap.getBoundingClientRect !== 'function') return;
      var top = wrap.getBoundingClientRect().top;
      // First estimate: footer 38 + status bar 26 + gaps/padding ~20 ...
      this.plTableH = Math.max(220, Math.floor(window.innerHeight - top - 92));
      // ... then correct by what actually overflows the fixed-height frame
      // (taller footers, e.g. page totals in invoices / people, push the
      // status bar out of view otherwise).
      var app = this;
      this.$nextTick(function () {
        var root = wrap.closest ? wrap.closest('.pl--fixed') : null;
        if (!root) return;
        var over = root.scrollHeight - root.clientHeight;
        if (over > 0) app.plTableH = Math.max(200, app.plTableH - over - 2);
      });
    },

    // ---- Excel-like cell selection ----
    cellPos(e) {
      var td = e.target && e.target.closest ? e.target.closest('td') : null;
      if (!td || !td.parentElement || td.parentElement.parentElement.tagName !== 'TBODY') return null;
      var tr = td.parentElement;
      return { r: Array.prototype.indexOf.call(tr.parentElement.children, tr), c: td.cellIndex };
    },
    onCellDown(e) {
      if (e.button !== 0) return;
      var pos = this.cellPos(e);
      if (!pos || e.target.closest('a, button, input, .v-simple-checkbox, .v-chip, .pl-key')) return;
      e.preventDefault();
      if (!this._cell) this._cell = { anchor: null, focus: null, extra: [], drag: false };
      var st = this._cell;
      if (e.shiftKey && st.anchor) {
        st.focus = pos;
      } else if (e.ctrlKey || e.metaKey) {
        if (st.anchor) st.extra = st.extra.concat(this.cellRect(st.anchor, st.focus));
        st.anchor = st.focus = pos;
      } else {
        st.extra = [];
        st.anchor = st.focus = pos;
      }
      st.drag = true;
      this._cellActive = true;
      this.renderCellSel();
    },
    // Keyboard selection only reacts while the table is the last thing clicked.
    onDocDown(e) {
      var wrap = this.plTableEl();
      if (wrap && !wrap.contains(e.target)) this._cellActive = false;
    },
    onCellOver(e) {
      var st = this._cell;
      if (!st || !st.drag) return;
      var pos = this.cellPos(e);
      if (!pos || (pos.r === st.focus.r && pos.c === st.focus.c)) return;
      st.focus = pos;
      this.renderCellSel();
    },
    onCellUp() {
      if (this._cell) this._cell.drag = false;
    },
    // Excel-like keys once a cell is selected:
    //   arrows            move the active cell
    //   Shift+arrows      extend the range
    //   Ctrl+arrows       jump to the first/last row or column
    //   Ctrl+Shift+arrows extend to the edge
    //   Ctrl+A            select every cell on the page
    //   Esc               clear
    onCellKey(e) {
      if (e.key === 'Escape') {
        this.clearCellSel();
        return;
      }
      var st = this._cell;
      if (!st || !st.anchor || !this._cellActive) return;
      var t = e.target;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      var wrap = this.plTableEl();
      var rows = wrap ? wrap.querySelectorAll('tbody > tr') : [];
      if (!rows.length) return;
      var maxR = rows.length - 1;
      var maxC = rows[0].cells.length - 1;
      var ctrl = e.ctrlKey || e.metaKey;

      if (ctrl && (e.key === 'a' || e.key === 'A')) {
        e.preventDefault();
        st.extra = [];
        st.anchor = { r: 0, c: 0 };
        st.focus = { r: maxR, c: maxC };
        this.renderCellSel();
        return;
      }

      var rtl = getComputedStyle(wrap).direction === 'rtl';
      var d = {
        ArrowUp: [-1, 0],
        ArrowDown: [1, 0],
        ArrowLeft: [0, rtl ? 1 : -1],
        ArrowRight: [0, rtl ? -1 : 1],
      }[e.key];
      if (!d) return;
      e.preventDefault();

      var from = e.shiftKey ? st.focus : st.focus || st.anchor;
      var r = from.r;
      var c = from.c;
      if (ctrl) {
        if (d[0]) r = d[0] < 0 ? 0 : maxR;
        if (d[1]) c = d[1] < 0 ? 0 : maxC;
      } else {
        r = Math.max(0, Math.min(maxR, r + d[0]));
        c = Math.max(0, Math.min(maxC, c + d[1]));
      }
      var next = { r: r, c: c };
      if (e.shiftKey) {
        st.focus = next;
      } else {
        st.extra = [];
        st.anchor = st.focus = next;
      }
      this.renderCellSel();
      var td = rows[r] && rows[r].cells[c];
      if (td && td.scrollIntoView) td.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    },
    cellRect(a, b) {
      var out = [];
      for (var r = Math.min(a.r, b.r); r <= Math.max(a.r, b.r); r++) {
        for (var c = Math.min(a.c, b.c); c <= Math.max(a.c, b.c); c++) out.push({ r: r, c: c });
      }
      return out;
    },
    renderCellSel() {
      var wrap = this.plTableEl();
      var st = this._cell;
      if (!wrap || !st || !st.anchor) return;
      wrap.querySelectorAll('td.pl-cell-sel').forEach(function (td) {
        td.classList.remove('pl-cell-sel');
      });
      var rows = wrap.querySelectorAll('tbody > tr');
      var seen = {};
      var count = 0;
      var nums = [];
      st.extra.concat(this.cellRect(st.anchor, st.focus)).forEach(function (p) {
        var key = p.r + ':' + p.c;
        if (seen[key]) return;
        seen[key] = true;
        var td = rows[p.r] && rows[p.r].cells[p.c];
        if (!td) return;
        td.classList.add('pl-cell-sel');
        count++;
        var txt = (td.innerText || '')
          .replace(/[۰-۹]/g, function (d) { return '۰۱۲۳۴۵۶۷۸۹'.indexOf(d); })
          .replace(/[,\s٬]/g, '');
        if (/^-?\d+(\.\d+)?$/.test(txt)) nums.push(parseFloat(txt));
      });
      var sum = nums.reduce(function (a, b) { return a + b; }, 0);
      this.cellSel = { count: count, numCount: nums.length, sum: sum, avg: nums.length ? sum / nums.length : 0 };
    },
    clearCellSel() {
      var wrap = this.plTableEl();
      if (wrap) {
        wrap.querySelectorAll('td.pl-cell-sel').forEach(function (td) {
          td.classList.remove('pl-cell-sel');
        });
      }
      this._cell = null;
      this.cellSel = { count: 0, numCount: 0, sum: 0, avg: 0 };
    },
  },
}
